import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Empty, Pagination, Space, Spin, Table, Tag, Typography } from 'antd';

const apiUrl = 'api/controle-acces';
const PAGE_SIZE = 20;

type AccessEvent = {
  id: string;
  optionLibelle: string;
  mode: string;
  scanneLe: string;
};

type AccessEventPage = {
  content: AccessEvent[];
  totalElements: number;
};

interface IEleveControleAccesProps {
  eleveId: string;
  anneeScolaireId: string;
  isActive: boolean;
}

const EleveControleAcces: React.FC<IEleveControleAccesProps> = ({ eleveId, anneeScolaireId, isActive }) => {
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const loadEvents = useCallback(async () => {
    if (!eleveId || !anneeScolaireId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<AccessEventPage>(`${apiUrl}/eleves/${eleveId}/evenements`, {
        params: { anneeScolaireId, page, size: PAGE_SIZE },
      });
      setEvents(response.data.content);
      setTotal(response.data.totalElements);
    } catch (_) {
      setError('L’historique des contrôles d’accès ne peut pas être chargé. Réessayez dans quelques instants.');
    } finally {
      setLoading(false);
    }
  }, [eleveId, anneeScolaireId, page]);

  useEffect(() => {
    if (isActive) loadEvents();
  }, [isActive, loadEvents]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          Historique des contrôles d’accès
        </Typography.Title>
        <Typography.Text type="secondary">Passages autorisés enregistrés au cours des 30 derniers jours.</Typography.Text>
      </div>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      <Spin spinning={loading}>
        {events.length === 0 && !loading ? (
          <Empty description="Aucun passage enregistré pour cet élève au cours des 30 derniers jours." />
        ) : (
          <Table<AccessEvent>
            rowKey="id"
            size="small"
            dataSource={events}
            pagination={false}
            columns={[
              {
                title: 'Date et heure',
                dataIndex: 'scanneLe',
                key: 'scanneLe',
                render: (value: string) => new Date(value).toLocaleString('fr-FR'),
              },
              { title: 'Contrôle d’accès', dataIndex: 'optionLibelle', key: 'optionLibelle' },
              {
                title: 'Mode',
                dataIndex: 'mode',
                key: 'mode',
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        )}
      </Spin>
      {total > PAGE_SIZE && (
        <Pagination
          current={page + 1}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={nextPage => setPage(nextPage - 1)}
          showSizeChanger={false}
          showTotal={count => `${count} passage(s)`}
          size="small"
        />
      )}
    </Space>
  );
};

export default EleveControleAcces;
